import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useScreenWidth } from "@msys/ui";
import { Alert, Box, Container, Stack } from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { getViewerDecisionRole } from "../../features/doc-items/helpers.js";
import { DecisionsInBulkProcessRef } from "../../features/doc-items/modals/DecisionsInBulkProcess.js";
import { QuoteAction } from "../../features/quotes/QuoteAction.js";
import { QuoteAnnotationsBanner } from "../../features/quote-annotations/QuoteAnnotationsBanner.js";
import { QuoteDecisionsInBulkProcess } from "../../features/quotes/QuoteDecisionsInBulkProcess.js";
import { QuotePreview } from "../../features/quotes/QuotePreview.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { DocShareBox } from "../documents/DocShareBox.js";
import {
  ProjectQuotePreviewHeaderFragment,
  ProjectQuotePreviewHeader_ProjectFragment,
  useProjectQuotePreviewQuery,
} from "./ProjectQuotePreview.generated.js";

const REFETCH_QUERIES = [namedOperations.Query.ProjectQuotePreview];

interface Props {
  projectId: string;
  quoteId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

const EMPTY_ARRAY: any[] = [];

export const ProjectQuotePreview = ({
  projectId,
  quoteId,
  submenuItems,
  activeSubmenuItem,
  prefixBreadcrumbs,
}: Props) => {
  const viewer = useUserData().currentUser!;
  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["Quote"]);
  const { isMinTablet } = useScreenWidth();

  const [previewContainer, setPreviewContainer] =
    React.useState<HTMLDivElement | null>(null);

  const client = useApolloClient();
  const query = useProjectQuotePreviewQuery({
    client,
    variables: {
      quoteId,
      projectId,
    },
    fetchPolicy: "no-cache",
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.quote)?.quote;
  const docTitle = doc?.title ?? "";

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const pathToDocList = `/projects/${projectId}/quotes`;
  const pathToDoc = `/projects/${projectId}/quotes/${quoteId}`;
  const pathToDocPage = `${pathToDoc}/preview`;

  const rootItem = doc?.rootItem;

  const isAgreed =
    (doc?.isPublished && doc?.isBinding && doc?.agreement === "YES") ?? false;
  const isViewersQuote =
    doc?.owningSystemOrganisationId === viewer.organisation.id;

  const viewerDecisionRole = getViewerDecisionRole(doc?.actors ?? []);

  const decisionsInBulkProcessRef =
    React.useRef<DecisionsInBulkProcessRef>(null);
  const startDecisionsInBulkProcess = React.useCallback(() => {
    decisionsInBulkProcessRef.current?.startDecisionsInBulkProcess();
  }, []);

  const docSharingEnabled = useFeature("DocSharing");
  const viewerPermissionDetailsEnabled = useFeature("ViewerPermissionDetails");

  return (
    <Page
      subtitle={project?.title}
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && doc ? (
          <QuoteSubHeader
            project={project}
            quote={doc}
            activeView="preview"
            canEdit={isViewersQuote}
            isReadOnly={isAgreed}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            hasSapS4HanaIntegration={hasSapS4HanaIntegration}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            expandedItemIds={undefined}
          />
        ) : undefined
      }
      action={
        project &&
        doc && (
          <QuoteAction
            doc={doc}
            project={project}
            onDiscardPendingChangesRefetchQueries={REFETCH_QUERIES}
            onDeclineChangesRefetchQueries={REFETCH_QUERIES}
            startDecisionsInBulkProcess={startDecisionsInBulkProcess}
            refetch={query.refetch}
            expandedItemIds={EMPTY_ARRAY}
            viewerDecisionRole={viewerDecisionRole}
          />
        )
      }
    >
      {doc && project && rootItem && (
        <PageContainer key={language} ref={setPreviewContainer}>
          <Container maxWidth="md">
            {viewerPermissionDetailsEnabled ? (
              <div>
                <h2>viewer permission details</h2>
                <hr />
                <textarea
                  style={{ width: "100%", height: 50 }}
                  readOnly
                  defaultValue={JSON.stringify(doc.viewerPermissions, null, 2)}
                ></textarea>
              </div>
            ) : null}

            {docSharingEnabled ? (
              <DocShareBox
                docType="QUOTE"
                docId={doc.id}
                projectId={project.id}
              />
            ) : null}

            <Stack direction={"column"} spacing={1}>
              <QuoteAnnotationsBanner />
              <ProjectQuotePreviewHeader quote={doc} project={project} />
              <QuotePreview
                projectId={projectId}
                quote={doc}
                container={isMinTablet ? previewContainer : undefined}
                expandedItemIds={EMPTY_ARRAY}
                refetch={query.refetch}
                hasSapS4HanaIntegration={hasSapS4HanaIntegration}
                canEdit={isViewersQuote && !isAgreed}
              />

              <QuoteDecisionsInBulkProcess
                ref={decisionsInBulkProcessRef}
                projectId={projectId}
                quoteId={doc.id}
                canFinalize={true}
                externalExpandedItemIds={EMPTY_ARRAY}
                handleComplete={query.refetch}
              />
            </Stack>
          </Container>
        </PageContainer>
      )}
    </Page>
  );
};

const ProjectQuotePreviewHeader = ({
  quote,
  project,
}: {
  quote: ProjectQuotePreviewHeaderFragment;
  project: ProjectQuotePreviewHeader_ProjectFragment;
}) => {
  const { t } = useTranslate(["Quote", "ProjectOverview"]);
  const viewer = useUserData().currentUser!;
  const isClientUser = viewer.organisation.isClientOrganisation;
  const showNonBindingTip = quote.isBinding === false && isClientUser;
  const nonBindingTip = showNonBindingTip
    ? t(
        "This document is for informational purposes only. To receive a binding quote document, please contact the craftsman via MeisterSystems chat.",
        { ns: "Quote" }
      )
    : undefined;

  const showClosedOpportunityAlert =
    quote.incomingRequest &&
    quote.incomingRequest.status !== "PUBLISHED" &&
    quote.incomingRequest.owningSystemOrganisationId !==
      viewer.organisation.id &&
    !quote.isPublished;

  return quote.decliningReason ||
    showNonBindingTip ||
    showClosedOpportunityAlert ? (
    <Stack direction="column" spacing={1}>
      {quote.decliningReason && (
        <Box>
          <Alert variant="outlined" severity="warning">
            {`${t("Declined", { ns: "Quote" })}: ${quote.decliningReason}`}
          </Alert>
        </Box>
      )}
      {showNonBindingTip && (
        <Box>
          <Alert variant="outlined" severity="info">
            {nonBindingTip}
          </Alert>
        </Box>
      )}
      {showClosedOpportunityAlert && (
        <Box>
          <Alert variant="outlined" severity="warning">
            {t("This opportunity is no longer accepting new quotes", {
              ns: "ProjectOverview",
            })}
          </Alert>
        </Box>
      )}
    </Stack>
  ) : null;
};
